if (!customElements.get('color-variant-selection')) {
  customElements.define(
    'color-variant-selection',
    class ColorVariantSelection extends HTMLElement {
      constructor() {
        super();
        this.variantInputs = this.querySelectorAll('input[type="radio"]');
        this.availabilityMap = null;
        this.variantPriceMap = this.buildVariantPriceMap();
        this.init();
      }

      buildVariantPriceMap() {
        // Use stored VariantData from discounted-price.liquid if available
        if (window.VariantData && typeof window.VariantData === 'object') {
          // Convert VariantData object to the expected format
          const priceMap = {};
          Object.keys(window.VariantData).forEach(variantId => {
            const variantData = window.VariantData[variantId];
            priceMap[variantId] = {
              price: variantData.price,
              originalPrice: variantData.originalPrice,
              discountedPrice: variantData.discountedPrice,
              compare_at_price: variantData.compare_at_price,
              available: variantData.available,
              sku: variantData.sku,
              barcode: variantData.barcode,
              hasDiscount: variantData.hasDiscount,
              // Additional formatted data for display
              originalPriceFormatted: variantData.originalPriceFormatted,
              discountedPriceFormatted: variantData.discountedPriceFormatted,
              compareAtPriceFormatted: variantData.compareAtPriceFormatted,
              discountPercentageText: variantData.discountPercentageText
            };
          });
          return priceMap;
        }
        
        // Fallback: Build from window.productVariants if VariantData not available
        const priceMap = {};
        if (window.productVariants && Array.isArray(window.productVariants)) {
          window.productVariants.forEach(variant => {
            const variantId = String(variant.id);
            const originalPrice = variant.price;
            const comparePrice = variant.compare_at_price || null;
            
            // Calculate discounted price from inputs if available
            // Check all inputs for this variant to get discount data
            const variantInputs = document.querySelectorAll(`input[value="${variantId}"]`);
            let discountedPrice = originalPrice;
            let hasDiscount = false;
            
            // Find input with discount data
            for (const input of variantInputs) {
              const originalPriceFormatted = input.getAttribute('data-original-price-formatted');
              const discountedPriceFormatted = input.getAttribute('data-discounted-price-formatted');
              
              if (discountedPriceFormatted && originalPriceFormatted) {
                // Extract numeric value from formatted string (e.g., "$115.00" -> 11500)
                const originalPriceCents = parseFloat(originalPriceFormatted.replace(/[^0-9.]/g, '')) * 100;
                const discountedPriceCents = parseFloat(discountedPriceFormatted.replace(/[^0-9.]/g, '')) * 100;
                
                if (originalPriceCents > discountedPriceCents) {
                  discountedPrice = discountedPriceCents;
                  hasDiscount = true;
                  break;
                }
              }
            }
            
            // Store all price data
            priceMap[variantId] = {
              price: hasDiscount ? discountedPrice : originalPrice, // Use discounted if available, otherwise original
              originalPrice: originalPrice, // Always store original
              discountedPrice: hasDiscount ? discountedPrice : null, // Store discounted only if discount exists
              compare_at_price: comparePrice,
              available: variant.available,
              sku: variant.sku,
              barcode: variant.barcode,
              hasDiscount: hasDiscount
            };
          });
        }
        return priceMap;
      }

      init() {
        this.variantInputs.forEach(input => {
          input.addEventListener('change', this.onVariantChange.bind(this));
        });
        
        const checkedInput = this.querySelector('input[type="radio"]:checked');
        if (checkedInput && !this.closest('.fbt__product-form')) {
          this.updateSeaterAvailability(checkedInput.dataset.variantTitle);
          
          const resolvedVariant = this.resolveMainProductVariant();
          if (resolvedVariant && resolvedVariant.id) {
            this.updateVariantDisplay(resolvedVariant.id);
            this.updateImagesByVariantOptions(resolvedVariant.id);
            this.updateURL(resolvedVariant.id);
          } else if (resolvedVariant) {
            this.updateButtonStateUnavailable();
          }
          
          this.updateOptionAvailability();
        }
      }

      getAvailabilityMap() {
        if (this.availabilityMap) return this.availabilityMap;
        
        const seaterVariants = window.shopify?.seaterVariants;
        if (!seaterVariants || !Array.isArray(seaterVariants)) return null;
        
        const colorLabels = ['color', 'colour', 'couleur', 'colore', 'farbe', '색', '色', 'färg', 'farve'];
        this.availabilityMap = {};
        
        seaterVariants.forEach(item => {
          if (!item?.seats || !item?.product?.variants) return;
          
          const { seats, product } = item;
          
          // Find color option
          const colorIndex = product.options?.findIndex(opt => 
            colorLabels.includes(opt.toLowerCase())
          );
          if (colorIndex === -1) return;
          
          // Collect available colors
          const colors = new Set();
          product.variants.forEach(v => {
            if (v.available && v[`option${colorIndex + 1}`]) {
              colors.add(v[`option${colorIndex + 1}`].toLowerCase());
            }
          });
          
          this.availabilityMap[seats] = Array.from(colors);
        });
        
        return this.availabilityMap;
      }

      onVariantChange(event) {
        if (!event.target.checked) return;

        const selectedInput = event.target;
        const variantId = selectedInput.value;
        const variantTitle = selectedInput.dataset.variantTitle;
        const productId = selectedInput.dataset.productId;

        this.updateDynamicHeading(variantTitle, productId, selectedInput);
        const productForm = this.closest('.fbt__product-form');
        if (productForm) {
          this.resolveAndApplyFBTVariant(productForm, selectedInput, variantId, variantTitle);
        } else {
          // For main product, resolve correct variant based on all selected options
          const resolvedVariant = this.resolveMainProductVariant();
          
          if (resolvedVariant && resolvedVariant.id) {
            this.updateFormInputs(resolvedVariant.id, productId);
            this.updateButtonAttributes(resolvedVariant.id, productId);
            // Pass the selectedInput so we can use its price data
            this.updateVariantDisplay(resolvedVariant.id, selectedInput);
            this.updateURL(resolvedVariant.id);
            this.dispatchVariantChangeEvent(resolvedVariant.id, variantTitle);
            this.updateImagesByVariantOptions(resolvedVariant.id);
          } else {
            this.updateButtonStateUnavailable();
          }
          
          this.updateSeaterAvailability(variantTitle);
          this.updateOptionAvailability();
        }
      }

      async resolveAndApplyFBTVariant(productForm, changedInput, fallbackVariantId, variantTitle) {
        try {
          const { handle } = this.getFBTProductHandle(productForm);
          const selections = this.getSelectedOptionTitles(productForm);

          let resolvedVariant = null;
          if (handle && selections.length > 0) {
            const product = await this.fetchProductJson(handle);
            if (product && Array.isArray(product.variants)) {
              resolvedVariant = product.variants.find(v => {
                const opts = [v.option1, v.option2, v.option3];
                for (let i = 0; i < selections.length; i++) {
                  if (!selections[i]) continue;
                  if ((opts[i] || '').toLowerCase() !== selections[i].toLowerCase()) return false;
                }
                return true;
              }) || null;
            }
          }

          const finalId = resolvedVariant ? String(resolvedVariant.id) : String(fallbackVariantId);
          this.updateFormInputs(finalId, changedInput.dataset.productId);
          this.updateButtonAttributes(finalId, changedInput.dataset.productId);

          if (resolvedVariant) {
            this.updateFBTVariantDisplayFromResolved(productForm, resolvedVariant);
          } else {
            this.updateFBTVariantDisplay(finalId, changedInput);
          }

          this.dispatchVariantChangeEvent(finalId, variantTitle);
        } catch (e) {
          // Fallback to basic behavior
          this.updateFormInputs(fallbackVariantId, changedInput.dataset.productId);
          this.updateButtonAttributes(fallbackVariantId, changedInput.dataset.productId);
          this.updateFBTVariantDisplay(fallbackVariantId, changedInput);
          this.dispatchVariantChangeEvent(fallbackVariantId, variantTitle);
        }
      }

      getFBTProductHandle(productForm) {
        // Try to get from product link within this FBT item
        const item = productForm.closest('.fbt__item');
        let href = '';
        if (item) {
          const link = item.querySelector('a[href*="/products/"]');
          href = link ? link.getAttribute('href') : '';
        }
        if (!href) return { handle: null };
        const match = href.match(/\/products\/([^\/?#]+)/);
        return { handle: match ? decodeURIComponent(match[1]) : null };
      }

      getSelectedOptionTitles(productForm) {
        const selections = [];
        const fieldsets = Array.from(productForm.querySelectorAll('fieldset'));
        fieldsets.forEach((fs, idx) => {
          const checked = fs.querySelector('input[type="radio"]:checked');
          const position = this.getFieldsetOptionPosition(fs, idx);
          if (checked) {
            const title = (checked.dataset.variantTitle || '').toString().trim();
            selections[position] = title.toLowerCase();
          } else {
            selections[position] = '';
          }
        });
        return selections;
      }

      getVariantById(variantId) {
        if (!window.productVariants || !Array.isArray(window.productVariants)) {
          return null;
        }
        
        const numericVariantId = parseInt(variantId, 10);
        return window.productVariants.find(v => v.id === numericVariantId) || null;
      }

      // Fieldsets for single-value options are not rendered (see
      // component-color-variant-selection.liquid), so fieldset DOM order does
      // not always match option1/option2/option3 positions. Each fieldset
      // carries data-option-index (1-based option position) to realign them;
      // skipped options stay unconstrained, which is safe because a skipped
      // option has the same value on every variant.
      getFieldsetOptionPosition(fieldset, fallbackIndex) {
        const parsed = parseInt(fieldset.dataset.optionIndex, 10);
        return Number.isNaN(parsed) ? fallbackIndex : parsed - 1;
      }

      resolveMainProductVariant() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        const selections = [];
        let allChecked = true;

        fieldsets.forEach((fs, idx) => {
          const checked = fs.querySelector('input[type="radio"]:checked');
          if (checked) {
            const title = (checked.dataset.variantTitle || '').toString().trim().toLowerCase();
            selections[this.getFieldsetOptionPosition(fs, idx)] = title;
          } else {
            allChecked = false;
          }
        });

        if (!allChecked) {
          return null;
        }

        if (window.productVariants && Array.isArray(window.productVariants)) {
          const matchingVariant = window.productVariants.find(variant => {
            const variantOptions = [
              variant.option1,
              variant.option2,
              variant.option3
            ].map(opt => (opt || '').toLowerCase());
            
            for (let i = 0; i < selections.length; i++) {
              if (selections[i] && variantOptions[i] !== selections[i]) {
                return false;
              }
            }
            return true;
          });
          
          if (matchingVariant) {
            return { id: String(matchingVariant.id), available: matchingVariant.available };
          }
        }

        // No matching variant found - this combination doesn't exist
        return { id: null, available: false };
      }

      async fetchProductJson(handle) {
        if (!handle) return null;
        if (!window.__fbtProductCache) window.__fbtProductCache = new Map();
        if (window.__fbtProductCache.has(handle)) return window.__fbtProductCache.get(handle);
        const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
        if (!res.ok) return null;
        const json = await res.json();
        window.__fbtProductCache.set(handle, json);
        return json;
      }

      updateFormInputs(variantId, productId) {
        // Check if we're in FBT context by looking for the FBT product form
        const productForm = this.closest('.fbt__product-form');
        if (productForm) {
          // FBT context - update hidden input with class 'fbt__variant-id'
          const hiddenInput = productForm.querySelector('.fbt__variant-id');
          if (hiddenInput) {
            hiddenInput.value = variantId;
          }
          return;
        }

        // Main product context - find form with data-type="add-to-cart-form"
        const form = this.closest('form[data-type="add-to-cart-form"]') || 
                    document.querySelector('form[data-type="add-to-cart-form"]');
        if (form) {
          const hiddenInput = form.querySelector('input[name="id"]');
          if (hiddenInput) {
            hiddenInput.value = variantId;
          }
          
          // Update first image and price for main product context  
          this.updateVariantDisplay(variantId);
        }
      }

      updateDynamicHeading(variantTitle, productId, selectedInput) {
        // Always scope to the fieldset of the changed input so only the correct label updates
        let dynamicHeading = null;
        if (selectedInput) {
          const fieldset = selectedInput.closest('fieldset');
          if (fieldset) dynamicHeading = fieldset.querySelector('.dynamic-variant-heading');
        }
        // Fallback within this component if needed
        if (!dynamicHeading) dynamicHeading = this.querySelector('.dynamic-variant-heading');
        if (dynamicHeading) dynamicHeading.textContent = variantTitle;
      }

      updateButtonAttributes(variantId, productId) {
        // Check if we're in FBT context by looking for the FBT product form
        const productForm = this.closest('.fbt__product-form');
        if (productForm) {
          // FBT context - update add to cart button
          const addToCartBtn = productForm.querySelector('.fbt-add-to-cart');
          if (addToCartBtn) {
            addToCartBtn.dataset.variantId = variantId;
          }
          const iconBtn = productForm.closest('.fbt__item')?.querySelector('.fbt-add-to-cart-icon');
          if (iconBtn) {
            iconBtn.dataset.variantId = variantId;
          }
        }
      }

      // Keeps both FBT buy buttons (text button inside the form and the
      // plus-icon overlay in the v2 layout) in sync with variant availability,
      // so out-of-stock variants can never be submitted to the cart.
      setFBTButtonsAvailability(productForm, available) {
        const addToCartBtn = productForm.querySelector('.fbt-add-to-cart');
        if (addToCartBtn) {
          addToCartBtn.disabled = !available;
          addToCartBtn.classList.toggle('fbt-add-to-cart-icon--disabled', !available);
          addToCartBtn.textContent = available ? 'Add to Cart' : 'Out of Stock';
        }

        const fbtItem = productForm.closest('.fbt__item');
        const iconBtn = fbtItem ? fbtItem.querySelector('.fbt-add-to-cart-icon') : null;
        if (iconBtn) {
          iconBtn.disabled = !available;
          iconBtn.classList.toggle('fbt-add-to-cart-icon--disabled', !available);
          iconBtn.setAttribute('aria-label', available ? 'Add to Cart' : 'Sold Out');
        }
      }

      updateVariantDisplay(variantId, clickedInput = null) {
        // Get variant price data from the map
        const variantData = this.variantPriceMap[String(variantId)];
        if (!variantData) {
          return;
        }

        // Get all prices directly from variant data map (no input checking needed)
        const variantPrice = variantData.price; // This is already the correct price (discounted if discount exists, otherwise original)
        const variantComparePrice = variantData.hasDiscount 
          ? variantData.originalPrice  // If discount exists, show original as compare price
          : variantData.compare_at_price; // Otherwise use compare_at_price
        const variantAvailable = variantData.available;
        const variantSku = variantData.sku;
        const variantBarcode = variantData.barcode;
        
        // Get MSRP from map
        const variantMsrp = window.variantMsrpMap?.[String(variantId)];
        
        // Find input only for other data (like formatted prices for display in dynamic-price-wrapper)
        const productForm = this.closest('form') || this.closest('.stroller-main-product') || document;
        const selectedInput = clickedInput || 
                              productForm.querySelector(`input[value="${variantId}"]:checked`) || 
                              productForm.querySelector(`input[value="${variantId}"]`) ||
                              this.querySelector(`input[value="${variantId}"]:checked`) || 
                              this.querySelector(`input[value="${variantId}"]`);
        
        // adds an attribute data-variant-selected="{{ variantId }}" to the closest stroller-main-product 
        // this will automatically update the variant display in the snippet-stroller-product-images.liquid
        const closestParent = this.closest('.stroller-main-product');
        const detailsContainer = closestParent
          ? closestParent.querySelector('.stroller-main-product__details')
          : document.querySelector('.stroller-main-product__details');

        if (detailsContainer) {
          detailsContainer.setAttribute('data-variant-selected', variantId);
        }

        if (closestParent) {
          const imageSets = closestParent.querySelectorAll('[data-image-set]');
          const hasVariantImageSets = Array.from(imageSets).some((el) => {
            const value = el.getAttribute('data-image-set');
            return value && /^\d+$/.test(value);
          });
          if (hasVariantImageSets) {
            closestParent.setAttribute('data-variant-selected', variantId);
          }
        }
        // Update price display (including B2B MSRP)
        if (variantPrice) {
          this.updatePriceDisplay(variantPrice, variantComparePrice, variantMsrp, variantId, selectedInput);
        }
        
        // Update SKU and UPC for B2B
        this.updateB2BDetails(variantSku, variantBarcode);

        // Update button availability state
        this.updateButtonState(variantAvailable, variantId);
      }

      updatePriceDisplay(price, comparePrice, msrpPrice, variantId, selectedInput) {
        const priceContainers = [
          document.querySelector('.stroller-main-product__price'),
          document.querySelector('.cro-pdp-sticky-bar__price')
        ].filter(Boolean);
        if (!priceContainers.length) return;

        priceContainers.forEach((priceContainer) => {
          this.updatePriceDisplayInContainer(priceContainer, price, comparePrice, msrpPrice, variantId, selectedInput);
        });
      }

      updatePriceDisplayInContainer(priceContainer, price, comparePrice, msrpPrice, variantId, selectedInput) {
        if (!priceContainer) return;

        const hasComparePrice = comparePrice && parseFloat(comparePrice) > parseFloat(price);
        
        // Update B2B MSRP price if present (already formatted from Liquid)
          const msrpPriceEl = priceContainer.querySelector('.price__msrp-price');
          const msrpContainer = priceContainer.querySelector('.price__msrp');
          if (msrpPriceEl) {
          if (msrpPrice) {
            // MSRP is already formatted from Liquid, just display it
            msrpPriceEl.textContent = msrpPrice;
            if (msrpContainer) msrpContainer.style.display = '';
          } else if (comparePrice && parseFloat(comparePrice) > 0) {
            // Fall back to compare_at_price if no MSRP metafield (needs formatting)
            msrpPriceEl.textContent = this.formatPrice(comparePrice);
            if (msrpContainer) msrpContainer.style.display = '';
          } else {
            // Hide MSRP if neither exists
            if (msrpContainer) msrpContainer.style.display = 'none';
          }
        }
        
        // Update B2B wholesale price
        // IMPORTANT: Wholesale price must always be the ORIGINAL (non-discounted) variant price.
        // `price` arg may be a discounted value (from `discount-ui-rs` retail promo), which is
        // not applicable to B2B customers; using it here would double-discount wholesale.
        const wholesalePriceEl = priceContainer.querySelector('.price__wholesale-price');
        if (wholesalePriceEl) {
          const variantDataForWholesale = this.variantPriceMap?.[String(variantId)];
          const wholesaleBasePrice = variantDataForWholesale?.originalPrice ?? price;
          wholesalePriceEl.textContent = this.formatPrice(wholesaleBasePrice);
        }
        
        // Update price classes for sale state
        const priceElement = priceContainer.querySelector('.price');
        if (priceElement) {
          if (hasComparePrice) {
            priceElement.classList.add('price--on-sale');
          } else {
            priceElement.classList.remove('price--on-sale');
          }
        }

        // Explicitly toggle sale/regular price visibility
        const saleDiv = priceContainer.querySelector('.price__sale');
        const regularDiv = priceContainer.querySelector('.price__regular');
        if (hasComparePrice) {
          if (saleDiv) saleDiv.style.display = '';
          if (regularDiv) regularDiv.style.display = 'none';
        } else {
          if (saleDiv) saleDiv.style.display = 'none';
          if (regularDiv) regularDiv.style.display = '';
        }

        // Update regular price
        const regularPriceEl = priceContainer.querySelector('.price__regular .price-item--regular');
        if (regularPriceEl) {
          regularPriceEl.textContent = this.formatPrice(price);
        }

        // Update sale price if exists
        const salePriceEl = priceContainer.querySelector('.price-item--sale');
        if (salePriceEl) {
          salePriceEl.textContent = this.formatPrice(price);
        }

        // Update compare at price (the <s> element in the sale div)
        const comparePriceEl = priceContainer.querySelector('.price__sale s.price-item--regular');
        if (comparePriceEl) {
          if (hasComparePrice) {
            comparePriceEl.textContent = this.formatPrice(comparePrice);
          }
        }

        // Update dynamic-price-wrapper if it exists
        const dynamicPriceWrapper = priceContainer.querySelector('.dynamic-price-wrapper');
        if (dynamicPriceWrapper) {
          // Get formatted prices from variantPriceMap (which uses stored VariantData)
          const variantData = this.variantPriceMap[String(variantId)];
          let originalPriceFormatted = null;
          let discountedPriceFormatted = null;
          let discountPercentageText = null;
          
          if (variantData) {
            // Use stored formatted prices from VariantData
            originalPriceFormatted = variantData.originalPriceFormatted;
            discountedPriceFormatted = variantData.discountedPriceFormatted;
            discountPercentageText = variantData.discountPercentageText;
          } else if (selectedInput) {
            // Fallback to input attributes if VariantData not available
            originalPriceFormatted = selectedInput.getAttribute('data-original-price-formatted');
            discountedPriceFormatted = selectedInput.getAttribute('data-discounted-price-formatted');
            discountPercentageText = selectedInput.getAttribute('data-discount-percentage-text');
          }
          
          // Update original price
          if (originalPriceFormatted) {
            const originalPriceElements = dynamicPriceWrapper.querySelectorAll('[data-original-price]');
            originalPriceElements.forEach(el => {
              el.textContent = originalPriceFormatted;
              // discount-display.js recomputes sale prices from these cents, so keep
              // them on the selected variant rather than the server-rendered one.
              if (variantData && Number.isFinite(Number(variantData.originalPrice))) {
                el.setAttribute('data-original-price', String(variantData.originalPrice));
              }
            });
          }

          // Update discounted price
          if (discountedPriceFormatted) {
            const discountedElements = dynamicPriceWrapper.querySelectorAll('[data-discounted-price]');
            discountedElements.forEach(el => {
              el.textContent = discountedPriceFormatted;
            });
          }

          // Update discount percentage text
          const discountPercentageElements = dynamicPriceWrapper.querySelectorAll('.price-discounted-percentage');
          if (discountPercentageText && discountPercentageElements.length > 0) {
            discountPercentageElements.forEach(el => {
              el.textContent = discountPercentageText;
            });
          } else if (!discountPercentageText && discountPercentageElements.length > 0) {
            // Hide if no discount
            discountPercentageElements.forEach(el => {
              el.textContent = '';
            });
          }

          // Update compare-at price spans (inside .price-regular / .price-no-discount only)
          // Use variant's actual compare_at_price (MSRP); display pre-formatted value from data so JS doesn't need to format
          const variantDataForCompare = this.variantPriceMap?.[String(variantId)];
          const actualCompareAtPrice = variantDataForCompare?.compare_at_price ?? comparePrice;
          const showCompareSpan = actualCompareAtPrice && parseFloat(actualCompareAtPrice) > parseFloat(price);
          const formattedCompare = variantDataForCompare?.compareAtPriceFormatted ?? (actualCompareAtPrice ? this.formatPrice(actualCompareAtPrice) : null);
          const compareWrappers = dynamicPriceWrapper.querySelectorAll('.price-regular .price-compare__wrapper, .price-no-discount .price-compare__wrapper');
          compareWrappers.forEach((wrapper) => {
            const compareSpan = wrapper.querySelector('.price-compare');
            if (compareSpan) {
              if (showCompareSpan && formattedCompare) {
                compareSpan.textContent = formattedCompare;
                wrapper.style.display = '';
              } else {
                wrapper.style.display = 'none';
              }
            }
          });
        }
      }
      
      updateB2BDetails(sku, barcode) {
        // Update SKU
        const skuEl = document.querySelector('.price__b2b-sku');
        if (skuEl) {
          if (sku) {
            skuEl.textContent = `SKU: ${sku}`;
            skuEl.style.display = '';
          } else {
            skuEl.style.display = 'none';
          }
        }
        
        // Update UPC/Barcode
        const upcEl = document.querySelector('.price__b2b-upc');
        if (upcEl) {
          if (barcode) {
            upcEl.textContent = `UPC: ${barcode}`;
            upcEl.style.display = '';
          } else {
            upcEl.style.display = 'none';
          }
        }
      }

      updateFBTVariantDisplay(variantId, selectedInput) {
        const productForm = this.closest('.fbt__product-form');
        if (!productForm) return;

        let variantPrice = selectedInput.dataset.variantPrice;
        let variantComparePrice = selectedInput.dataset.variantComparePrice;
        let variantMsrp = selectedInput.dataset.variantMsrp;
        let variantSku = selectedInput.dataset.variantSku;
        let variantBarcode = selectedInput.dataset.variantBarcode;
        let variantAvailable = selectedInput.dataset.variantAvailable === 'true';
        let variantImage = selectedInput.dataset.variantImage;
        
        if (String(variantId) !== String(selectedInput.value)) {
          const variantData = this.getVariantById(variantId);
          if (variantData) {
            variantPrice = variantData.price;
            variantComparePrice = variantData.compare_at_price;
            variantAvailable = variantData.available;
            variantSku = variantData.sku;
            variantBarcode = variantData.barcode;
            if (variantData.featured_image && variantData.featured_image.src) {
              variantImage = variantData.featured_image.src;
            }
          }
        }

        const fbtItem = productForm.closest('.fbt__item');
        if (!fbtItem) return;

        // Update FBT B2B pricing
        const fbtB2bPricing = fbtItem.querySelector('.fbt__b2b-pricing');
        if (fbtB2bPricing) {
          // Update MSRP (already formatted from Liquid)
          const msrpEl = fbtB2bPricing.querySelector('.fbt__msrp');
          if (msrpEl) {
            if (variantMsrp) {
              msrpEl.textContent = `MSRP ${variantMsrp}`;
              msrpEl.style.display = '';
            } else {
              msrpEl.style.display = 'none';
            }
          }
          
          // Update wholesale price
          const wholesaleEl = fbtB2bPricing.querySelector('.fbt__wholesale');
          if (wholesaleEl && variantPrice) {
            const tagEl = wholesaleEl.querySelector('.fbt__wholesale-tag');
            const tagText = tagEl ? tagEl.outerHTML : '<span class="fbt__wholesale-tag">Wholesale</span>';
            wholesaleEl.innerHTML = `${tagText} ${this.formatPrice(variantPrice)}`;
          }
        } else {
          // Non-B2B: Update regular FBT item price
          const priceElement = fbtItem.querySelector('.fbt__item-price');
          if (priceElement && variantPrice) {
            priceElement.textContent = this.formatPrice(variantPrice);
          }
        }

        // Update FBT item image
        if (variantImage) {
          const imageElement = fbtItem.querySelector('.fbt__item-image img');
          if (imageElement) {
            imageElement.src = variantImage;
          }
        }

        // Update FBT button state
        this.setFBTButtonsAvailability(productForm, variantAvailable);
      }

      updateFBTVariantDisplayFromResolved(productForm, variant) {
        const fbtItem = productForm.closest('.fbt__item');
        if (!fbtItem) return;

        // Update B2B pricing if present
        const fbtB2bPricing = fbtItem.querySelector('.fbt__b2b-pricing');
        if (fbtB2bPricing) {
          // Update wholesale price
          const wholesaleEl = fbtB2bPricing.querySelector('.fbt__wholesale');
          if (wholesaleEl && variant.price) {
            const tagEl = wholesaleEl.querySelector('.fbt__wholesale-tag');
            const tagText = tagEl ? tagEl.outerHTML : '<span class="fbt__wholesale-tag">Wholesale</span>';
            wholesaleEl.innerHTML = `${tagText} ${this.formatPrice(variant.price)}`;
          }
          // Note: MSRP from metafield isn't available in variant.js data, 
          // so we can't update it here. The initial MSRP stays until page reload.
        } else {
          // Non-B2B: Update regular price
          const priceElement = fbtItem.querySelector('.fbt__item-price');
          if (priceElement) priceElement.textContent = this.formatPrice(variant.price);
        }
        
        // Update image
        const imageElement = fbtItem.querySelector('.fbt__item-image img');
        const imgSrc = (variant.featured_image && variant.featured_image.src) ? variant.featured_image.src : '';
        if (imageElement && imgSrc) imageElement.src = imgSrc;
        
        // Update availability
        this.setFBTButtonsAvailability(productForm, variant.available);
      }

      updateButtonState(available, variantId) {
        // Check if we're in FBT context by looking for the FBT product form
        const productForm = this.closest('.fbt__product-form');
        if (productForm) {
          this.setFBTButtonsAvailability(productForm, available);
          return;
        }

        // Main product context - update main add to cart button
        const addToCartBtn = document.querySelector('#add-to-cart-btn');
        const buttonText = addToCartBtn ? addToCartBtn.querySelector('span') : null;

        if (!addToCartBtn) return;

        if (available) {
          // Product is available
          addToCartBtn.disabled = false;
          if (buttonText) {
            buttonText.textContent = 'Add to Cart';
          }

          // Update hidden variant input (main product context only)
          const variantInput = document.querySelector('input[name="id"][type="hidden"]');
          if (variantInput) {
            variantInput.value = variantId;
          }
        } else {
          // Product is out of stock
          addToCartBtn.disabled = true;
          if (buttonText) {
            buttonText.textContent = 'Out of Stock';
          }
        }

        this.syncMainKlaviyoBisTrigger(addToCartBtn, available);
      }

      // Shows either the ATC button or the Klaviyo "Notify Me When Available"
      // trigger — never both. Only acts when the trigger anchor is rendered
      // next to the button (snippet-add-to-cart-form.liquid).
      //
      // Klaviyo's dynamic BIS button uses the .klaviyo-bis-trigger anchor as
      // its placement target: it hides the anchor with inline
      // display:none!important and injects its real button in
      // #klaviyo-bis-button-container next to it. The anchor is only a
      // pre-Klaviyo fallback; the injected container's visibility is driven
      // by the bis-variant-available class on <html> (see base.css) because
      // Klaviyo's own variant detection lags behind the custom pickers.
      syncMainKlaviyoBisTrigger(addToCartBtn, available) {
        if (!addToCartBtn) return;
        const scope = addToCartBtn.closest('form') || addToCartBtn.parentElement;
        const bisTrigger = scope ? scope.querySelector('.klaviyo-bis-trigger') : null;
        if (!bisTrigger) return;

        const quantityInput = scope.querySelector('quantity-input.quantity');

        if (available) {
          addToCartBtn.style.display = '';
          bisTrigger.classList.add('klaviyo-bis-trigger--theme-hidden');
          bisTrigger.classList.remove('klaviyo-bis-trigger--theme-visible');
          document.documentElement.classList.add('bis-variant-available');
          if (quantityInput) quantityInput.classList.remove('hide');
        } else {
          addToCartBtn.style.display = 'none';
          bisTrigger.classList.remove('klaviyo-bis-trigger--theme-hidden');
          bisTrigger.classList.add('klaviyo-bis-trigger--theme-visible');
          document.documentElement.classList.remove('bis-variant-available');
          if (quantityInput) quantityInput.classList.add('hide');
        }
      }

      updateButtonStateUnavailable() {        
        const addToCartBtn = document.querySelector('#add-to-cart-btn');
        if (!addToCartBtn) return;
        
        const buttonText = addToCartBtn.querySelector('span');

        addToCartBtn.disabled = true;
        if (buttonText) {
          buttonText.textContent = 'Out of Stock';
        }

        // A non-existent option combination has no variant to be notified
        // about, so show the disabled button rather than the Klaviyo trigger.
        const scope = addToCartBtn.closest('form') || addToCartBtn.parentElement;
        const bisTrigger = scope ? scope.querySelector('.klaviyo-bis-trigger') : null;
        if (bisTrigger) {
          addToCartBtn.style.display = '';
          bisTrigger.classList.add('klaviyo-bis-trigger--theme-hidden');
          bisTrigger.classList.remove('klaviyo-bis-trigger--theme-visible');
          // No variant exists for this combination, so there is nothing to be
          // notified about — hide Klaviyo's injected button as well.
          document.documentElement.classList.add('bis-variant-available');
        }
      }

      formatPrice(priceInCents) {
        // Simple price formatting - adjust based on your currency settings
        const price = parseFloat(priceInCents) / 100;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(price);
      }

      updateSeaterAvailability(selectedColor) {
        if (!selectedColor) return;
        
        const availabilityMap = this.getAvailabilityMap();
        if (!availabilityMap) return;

        const color = selectedColor.toLowerCase().trim();
        
        document.querySelectorAll('.number-of-seats__option').forEach(option => {
          if (option.tagName === 'SPAN') return;
          
          const match = option.textContent.match(/(\d+)-Seater/);
          if (!match) return;
          
          const seats = parseInt(match[1], 10);
          const availableColors = availabilityMap[seats];
          
          if (availableColors?.includes(color)) {
            option.classList.remove('number-of-seats__option--unavailable');
          } else {
            option.classList.add('number-of-seats__option--unavailable');
          }
        });
      }

      updateOptionAvailability() {        
        const variants = window.productVariants;
        
        if (!variants || !Array.isArray(variants)) return;

        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        
        const currentSelections = [];
        fieldsets.forEach((fieldset, index) => {
          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            currentSelections[this.getFieldsetOptionPosition(fieldset, index)] = checkedInput.dataset.variantTitle?.toLowerCase().trim();
          }
        });

        fieldsets.forEach((fieldset, fieldsetIndex) => {
          const allInputs = fieldset.querySelectorAll('input[type="radio"]');
          const optionPosition = this.getFieldsetOptionPosition(fieldset, fieldsetIndex);
          
          allInputs.forEach(input => {
            const optionValue = input.dataset.variantTitle?.toLowerCase().trim();
            if (!optionValue) return;

            const testSelections = [...currentSelections];
            testSelections[optionPosition] = optionValue;

            const hasAvailableVariant = variants.some(variant => {
              if (!variant.available) return false;

              const variantOptions = [
                variant.option1?.toLowerCase().trim(),
                variant.option2?.toLowerCase().trim(),
                variant.option3?.toLowerCase().trim()
              ];

              return testSelections.every((selection, idx) => {
                if (!selection) return true;
                return variantOptions[idx] === selection;
              });
            });

            const label = input.closest('label');
            if (label) {
              if (hasAvailableVariant) {
                label.classList.remove('color-selection__option--unavailable');
                label.classList.remove('size-selection__option--unavailable');
              } else {
                if (label.classList.contains('color-selection__option')) {
                  label.classList.add('color-selection__option--unavailable');
                } else if (label.classList.contains('size-selection__option')) {
                  label.classList.add('size-selection__option--unavailable');
                }
              }
            }
          });
        });
      }

      updateURL(variantId) {
        if (!variantId || this.closest('.fbt__product-form')) return;
        if (typeof window.updateProductVariantUrl === 'function') {
          window.updateProductVariantUrl(variantId);
        }
      }

      dispatchVariantChangeEvent(variantId, variantTitle) {
        // Dispatch custom event for any other listeners
        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: {
            variant: {
              id: variantId,
              title: variantTitle
            },
            source: this
          }
        }));
      }

      getVariantOptionValues() {
        const selections = [];
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        
        fieldsets.forEach(fieldset => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) {
            selections.push(checked.dataset.variantTitle.trim());
          }
        });
        
        return selections;
      }

      updateImagesByVariantOptions(variantId) {
        const closestParent = this.closest('.stroller-main-product');
        if (!closestParent) return;

        const variantOptions = this.getVariantOptionValues();
        if (variantOptions.length === 0) return;

        const allImageSets = closestParent.querySelectorAll('[data-image-set]');
        
        allImageSets.forEach((set) => {
          const setId = set.getAttribute('data-image-set');
          if (setId && /^\d+$/.test(setId)) {
            if (String(setId) === String(variantId)) {
              set.style.display = 'block';
            } else {
              set.style.display = 'none';
            }
          }
        });

        const imageSets = closestParent.querySelectorAll(`[data-image-set="${variantId}"]`);
        if (imageSets.length === 0) return;
        
        imageSets.forEach(imageSet => {
          const images = imageSet.querySelectorAll('img[alt]:not(.stroller-product-images__collaboration-icon-image), video[alt]:not(.stroller-product-images__collaboration-icon-image)');
          let hasVisibleMedia = false;
          
          images.forEach(media => {
            const altText = media.getAttribute('alt');
            const matches = this.matchImageToOptions(altText, variantOptions);
            
            if (matches) {
              const mediaWrapper = media.closest('.stroller-product-images__item, .stroller-product-images__slide');
              if (mediaWrapper) {
                mediaWrapper.style.display = '';
                hasVisibleMedia = true;
              } else if (media.parentElement) {
                media.parentElement.style.display = '';
                hasVisibleMedia = true;
              }
            } else {
              const mediaWrapper = media.closest('.stroller-product-images__item, .stroller-product-images__slide');
              if (mediaWrapper) {
                mediaWrapper.style.display = 'none';
              } else if (media.parentElement) {
                media.parentElement.style.display = 'none';
              }
            }
          });

          if (!hasVisibleMedia) {
            images.forEach(media => {
              const mediaWrapper = media.closest('.stroller-product-images__item, .stroller-product-images__slide');
              if (mediaWrapper) {
                mediaWrapper.style.display = '';
              } else if (media.parentElement) {
                media.parentElement.style.display = '';
              }
            });
          }
        });
      }

      matchImageToOptions(imageAlt, variantOptions) {
        if (!imageAlt || variantOptions.length === 0) return false;

        // Alt text may list multiple variant combos separated by commas,
        // e.g. "Small / Black, Medium / Black, Large / Black".
        // Matching must stay in sync with the Liquid filter in
        // snippets/snippet-stroller-product-images.liquid.
        const combos = imageAlt.toLowerCase().split(',').map(s => s.trim()).filter(Boolean);

        return combos.some(combo => {
          const altParts = combo.split('/').map(s => s.trim());

          if (altParts.length === variantOptions.length) {
            return variantOptions.every((opt, idx) => 
              altParts[idx] === opt.toLowerCase()
            );
          }

          if (altParts.length === 1) {
            return altParts[0] === variantOptions[0].toLowerCase();
          }

          if (altParts.length > 1 && altParts.length <= variantOptions.length) {
            return altParts.every(altPart => 
              variantOptions.some(opt => opt.toLowerCase() === altPart)
            );
          }

          return false;
        });
      }
    }
  );
}